/**
 * A/B Test Purchase Widget
 * 
 * Handles the new quantity + subscription checkbox interface
 * Integrates with existing form elements and maintains compatibility
 */

// Avoid redeclaration when the script is included multiple times (e.g., Replo proxy)
window.ABTestPurchaseWidget = window.ABTestPurchaseWidget || class ABTestPurchaseWidget {
    constructor() {
      this.widget = null;
      this.isInitialized = false;
      this.isSubscriptionEnabled = false;
      this.variantPlanMap = {};
      this.variantPricingMap = {};
      
      // Bind methods to maintain context
      this.handlePurchaseModeChange = this.handlePurchaseModeChange.bind(this);
      this.updatePriceDisplay = this.updatePriceDisplay.bind(this);
      this.syncSubscriptionAvailability = this.syncSubscriptionAvailability.bind(this);
      
      this.init();
    }
  
    init() {
      // Wait for DOM to be ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.setupWidget());
      } else {
        this.setupWidget();
      }
    }
  
    setupWidget() {
      this.widget = document.getElementById('ab-test-purchase-widget');
      
      if (!this.widget) {
        console.log('AB Test Widget: Widget container not found');
        return;
      }
  
      console.log('AB Test Widget: Found widget, initializing...', this.widget);

      // Load pricing/plan maps emitted by the snippet
      try {
        const planScript = document.querySelector('script[data-variant-plan-map]');
        if (planScript) this.variantPlanMap = JSON.parse(planScript.textContent || '{}');
        const priceScript = document.querySelector('script[data-variant-pricing-map]');
        if (priceScript) this.variantPricingMap = JSON.parse(priceScript.textContent || '{}');
      } catch (e) {
        console.warn('AB Test Widget: Failed parsing maps', e);
      }

      this.bindEventListeners();
      this.setInitialState();
      this.isInitialized = true;
      
      console.log('AB Test Widget: Initialized successfully');
    }
  
    bindEventListeners() {
      console.log('AB Test Widget: Binding event listeners...');
      
      // Purchase mode radios (one_time vs subscription)
      const modeRadios = this.widget.querySelectorAll('[name="tec_purchase_mode"]');
      modeRadios.forEach((radio) => {
        radio.addEventListener('change', this.handlePurchaseModeChange);
      });
  
      // Delivery frequency selector (if present in original widget)
      const deliverySelector = document.getElementById('subscr_select');
      if (deliverySelector) {
        deliverySelector.addEventListener('change', (e) => {
          this.updateHiddenSellingPlan(e.target.value);
        });
      }

      // Guarantee selling_plan is set at submit time when subscription is chosen
      const form = this.widget.closest('form');
      if (form) {
        form.addEventListener('submit', () => {
          if (this.isSubscriptionEnabled) {
            const planId = this.getCurrentVariantPlanId();
            if (planId) {
              this.updateHiddenSellingPlan(planId);
              this.updateHiddenPurchaseOptions(true);
            }
          }
        });
      }
  
      // Listen for variant changes to update prices
      document.addEventListener('change', (e) => {
        if (e.target.closest('variant-picker') || e.target.matches('[name="id"]')) {
          setTimeout(() => {
            this.updatePriceDisplay();
            // Keep subscription availability in sync with selected variant
            this.syncSubscriptionAvailability();
            // If user had subscription enabled before the variant change and it's available,
            // re-apply subscription mode and selling plan for the new variant
            if (this.isSubscriptionEnabled) {
              const planId = this.getCurrentVariantPlanId();
              if (planId) {
                const subRadio = this.widget.querySelector('input[name="tec_purchase_mode"][value="subscription"]');
                if (subRadio && !subRadio.checked) {
                  subRadio.checked = true;
                }
                this.updateHiddenPurchaseOptions(true);
                this.updateHiddenSellingPlan(planId);
              }
            }
            this.updatePriceDisplay();
          }, 100);
        }
      });
      
      console.log('AB Test Widget: Event listeners bound successfully');
    }
    
    handlePurchaseModeChange(event) {
      const value = event.target.value;
      this.isSubscriptionEnabled = value === 'subscription';

      // Visual selected state
      this.widget.querySelectorAll('.ab-test-option').forEach(option => {
        option.classList.remove('ab-test-option--selected', 'bg-[#F7F4ED]');
        const dot = option.querySelector('.ab-test-radio-dot');
        if (dot) dot.style.transform = 'scale(0)';
      });
      const currentOption = event.target.closest('.ab-test-option');
      if (currentOption) {
        currentOption.classList.add('ab-test-option--selected', 'bg-[#F7F4ED]');
        const dot = currentOption.querySelector('.ab-test-radio-dot');
        if (dot) dot.style.transform = 'scale(1)';
      }

      // Sync underlying hidden purchase option radios
      this.updateHiddenPurchaseOptions(this.isSubscriptionEnabled);

      // If subscription, ensure a valid selling plan is selected
      if (this.isSubscriptionEnabled) {
        const planId = this.getCurrentVariantPlanId();
        this.updateHiddenSellingPlan(planId || '');
      } else {
        this.updateHiddenSellingPlan('');
      }

      this.updatePriceDisplay();
    }
  
    // Ensure the subscription option respects availability of the selected variant
    syncSubscriptionAvailability() {
      const subscriptionRadio = this.widget.querySelector('input[name="tec_purchase_mode"][value="subscription"]');
      if (!subscriptionRadio) return;

      const hasPlan = !!this.getCurrentVariantPlanId();
      subscriptionRadio.disabled = !hasPlan;

      if (!hasPlan && subscriptionRadio.checked) {
        // Force one-time if no subscription available
        const oneTime = this.widget.querySelector('input[name="tec_purchase_mode"][value="one_time"]');
        if (oneTime && !oneTime.checked) {
          oneTime.checked = true;
          oneTime.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }
  
    updateHiddenPurchaseOptions(isSubscription) {
      const subscriptionRadio = document.getElementById('subscr');
      const oneTimeRadio = document.getElementById('one_time');
      
      if (isSubscription && subscriptionRadio && !subscriptionRadio.checked) {
        subscriptionRadio.checked = true;
        subscriptionRadio.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (!isSubscription && oneTimeRadio && !oneTimeRadio.checked) {
        oneTimeRadio.checked = true;
        oneTimeRadio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  
    updateHiddenSellingPlan(sellingPlanId) {
      const sellingPlanInput = document.querySelector('[name="selling_plan"]');
      const hiddenSelector = document.getElementById('subscr_select');
      
      if (sellingPlanInput) {
        sellingPlanInput.value = sellingPlanId || '';
      }
      
      if (hiddenSelector) {
        hiddenSelector.value = sellingPlanId || '';
      }
    }
  
    updatePriceDisplay() {
      const vid = this.getCurrentVariantId();
      const pricing = this.variantPricingMap[vid] || {};

      const oneContainer = this.widget.querySelector('.ab-test-price-container[data-mode="one_time"]');
      const subContainer = this.widget.querySelector('.ab-test-price-container[data-mode="subscription"]');
      const oneRibbon = this.widget.querySelector('.ab-test-ribbon[data-mode="one_time"]');
      const subRibbon = this.widget.querySelector('.ab-test-ribbon[data-mode="subscription"]');

      const price = typeof pricing.price === 'number' ? pricing.price : null;
      const cap = typeof pricing.compare_at_price === 'number' ? pricing.compare_at_price : null;
      const sub = typeof pricing.subscription_price === 'number' ? pricing.subscription_price : null;

      if (oneContainer && price !== null) {
        const priceEl = oneContainer.querySelector('.ab-test-price');
        const capEl = oneContainer.querySelector('.ab-test-price-original');
        if (priceEl) {
          priceEl.textContent = this.formatPrice(price);
          try { priceEl.setAttribute('aria-label', 'Price'); } catch (e) {}
        }
        if (capEl) {
          if (cap) {
            const formattedPrice = this.formatPrice(cap);
            this.updateAccessibleStrikePrice(capEl, formattedPrice, true);
          } else {
            this.updateAccessibleStrikePrice(capEl, null, false);
          }
        }
        if (oneRibbon) {
          const pct = cap ? Math.max(0, Math.round(((cap - price) / cap) * 100)) : 0;
          oneRibbon.textContent = String(pct);
        }
      }

      if (subContainer) {
        const priceEl = subContainer.querySelector('.ab-test-price');
        const capEl = subContainer.querySelector('.ab-test-price-original');
        const val = sub ?? price ?? 0;
        if (priceEl) {
          priceEl.textContent = this.formatPrice(val);
          try { priceEl.setAttribute('aria-label', 'Discounted price'); } catch (e) {}
        }
        if (capEl) {
          if (cap) {
            const formattedPrice = this.formatPrice(cap);
            this.updateAccessibleStrikePrice(capEl, formattedPrice, true);
          } else {
            this.updateAccessibleStrikePrice(capEl, null, false);
          }
        }
        if (subRibbon) {
          const base = cap || 0;
          const pct = base > 0 ? Math.max(0, Math.round(((base - val) / base) * 100)) : 0;
          subRibbon.textContent = String(pct);
        }
      }

      // Update Add to Cart button label to reflect selected mode
      const selectedCents = this.isSubscriptionEnabled ? (sub ?? price ?? 0) : (price ?? 0);
      const label = document.querySelector('.purchase-details__buttons .action_button .original_label');
      if (label && typeof selectedCents === 'number') {
        const text = label.textContent || '';
        const base = 'Add to cart - ' + this.formatPrice(selectedCents);
        // Replace everything after the dash if present; else set whole text
        const dashIndex = text.indexOf('-');
        label.textContent = dashIndex !== -1 ? text.slice(0, dashIndex + 1) + ' ' + this.formatPrice(selectedCents) : base;
      }

      // Update generic current price displays used elsewhere on the page
      const currentPriceEls = document.querySelectorAll('.current_price, .tec-current-price');
      currentPriceEls.forEach(el => {
        const money = el.querySelector?.('.money') || el;
        money.textContent = this.formatPrice(selectedCents);
        try { money.setAttribute('aria-label', this.isSubscriptionEnabled ? 'Discounted price' : 'Price'); } catch (e) {}
      });
    }

    toggleSubscriptionDetails(show) {
      const details = document.getElementById('ab-test-subscription-details');
      if (details) {
        if (show) {
          details.classList.remove('hidden');
        } else {
          details.classList.add('hidden');
        }
      }
    }
  
    triggerVariantChange() {
      // This might be needed if switching to 4 months requires a different variant
      // For now, we just dispatch a general change event
      const event = new CustomEvent('ab_test_quantity_changed', {
        bubbles: true,
        detail: {
          quantityType: this.currentQuantityType,
          quantity: this.currentQuantityType === '2_sets_free_dish_hair_body_oil' ? 2 : 1
        }
      });
      document.dispatchEvent(event);
    }
  
    formatPrice(priceInCents) {
      // Simple price formatting - adjust based on your shop's currency format
      const price = priceInCents / 100;
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(price);
    }

    // Helper function to create accessible strike-down price HTML structure
    createAccessibleStrikePrice(moneyText) {
      // Returns HTML string with proper structure: <span aria-label="Original price"><span class="money">$XX.XX</span></span>
      const escaped = String(moneyText).replace(/</g, '&lt;').replace(/>/g, '&gt;');
      return `<span aria-label="Original price"><span class="money">${escaped}</span></span>`;
    }

    // Helper function to update or create accessible strike-down price element
    updateAccessibleStrikePrice(element, priceText, shouldShow) {
      if (!element) return;
      
      if (shouldShow && priceText) {
        // Ensure proper structure exists
        let wrapper = element.querySelector('span[aria-label="Original price"]');
        if (!wrapper) {
          // Create the full structure if it doesn't exist
          element.innerHTML = this.createAccessibleStrikePrice(priceText);
        } else {
          // Update only the money text, preserving structure
          const moneyEl = wrapper.querySelector('.money');
          if (moneyEl) {
            moneyEl.textContent = priceText;
          } else {
            // Fallback: recreate structure
            element.innerHTML = this.createAccessibleStrikePrice(priceText);
          }
        }
        element.style.visibility = 'visible';
        element.classList.remove('hidden');
        element.removeAttribute('aria-hidden');
        try { element.setAttribute('aria-label', 'Original price'); } catch(e) {}
      } else {
        element.style.visibility = 'hidden';
        element.classList.add('hidden');
        element.setAttribute('aria-hidden', 'true');
      }
    }
  
    setInitialState() {
      console.log('AB Test Widget: Setting initial state');
      // Prefer subscription by default when available
      const subRadio = this.widget.querySelector('input[name="tec_purchase_mode"][value="subscription"]');
      const oneTime = this.widget.querySelector('input[name="tec_purchase_mode"][value="one_time"]');
      const hasPlan = !!this.getCurrentVariantPlanId();

      if (hasPlan && subRadio) {
        subRadio.checked = true;
        this.isSubscriptionEnabled = true;
        this.updateHiddenPurchaseOptions(true);
        this.updateHiddenSellingPlan(this.getCurrentVariantPlanId());
      } else if (oneTime) {
        oneTime.checked = true;
        this.isSubscriptionEnabled = false;
        this.updateHiddenPurchaseOptions(false);
        this.updateHiddenSellingPlan('');
      }

      // Ensure subscription respects availability for the selected variant
      this.syncSubscriptionAvailability();

      // Update initial prices
      this.updatePriceDisplay();

      // Some legacy listeners update labels after our changes; refresh again shortly
      setTimeout(() => {
        this.updatePriceDisplay();
      }, 350);

      // Handle dynamic benefit copy via query param: free_gifts_copy=1
      const params = new URLSearchParams(window.location.search);
      if (params.get('free_gifts_copy') === '1') {
        const dyn = this.widget.querySelector('.ab-benefit-dynamic');
        if (dyn) dyn.innerHTML = '<strong>FREE</strong> Gifts With Every Order';
      }
    }
  
    // Public method to get current state
    getCurrentState() {
      return {
        quantityType: this.currentQuantityType,
        quantity: this.currentQuantityType === '2_sets_free_dish_hair_body_oil' ? 2 : 1,
        isSubscription: this.isSubscriptionEnabled,
        isInitialized: this.isInitialized
      };
    }
  
    // Public method to manually trigger state update
    refresh() {
      if (this.isInitialized) {
        this.updatePriceDisplay();
      }
    }

    getCurrentVariantId() {
      const input = document.querySelector('[name="id"]');
      return input ? String(input.value) : '';
    }

    getCurrentVariantPlanId() {
      const vid = this.getCurrentVariantId();
      const arr = this.variantPlanMap[vid] || [];
      return Array.isArray(arr) && arr.length > 0 ? arr[0] : '';
    }
  }
  
  // Initialize the widget when DOM is ready
  
  function initializeABTestWidget() {
    if (document.getElementById('ab-test-purchase-widget')) {
      // Reuse existing instance if present, otherwise create one
      if (!window.ABTestWidget || !window.ABTestWidget.isInitialized) {
        window.ABTestWidget = new window.ABTestPurchaseWidget();
      } else {
        // If already initialized (e.g., script included twice), just refresh prices/UI
        try { window.ABTestWidget.refresh && window.ABTestWidget.refresh(); } catch (e) {}
      }
    }
  }
  
  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeABTestWidget);
  } else {
    initializeABTestWidget();
  }
  
  // Also listen for dynamic content loading
  document.addEventListener('content:loaded', initializeABTestWidget);
  
  // Export for module systems if needed
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = window.ABTestPurchaseWidget;
  }